import React, { useState, useRef, useEffect } from 'react'

import Field from '../../Field/Field'
import TextInput from '../../TextInput/TextInput'
import Textarea from '../../Textarea/Textarea'
import Tooltip from '../../Tooltip/Tooltip'
import FieldLabel from '../../FieldLabel/FieldLabel'
import Checkbox from '../../Checkbox/Checkbox'
import Radio from '../../Radio'
import Help from '../../Help/Help'
import { fieldUidRegex, getContentTypeFieldDataTestId, CONTENT_TYPE_FIELD_ACTION_IDS, NonLocalizableDom, handleToggleClick } from '../helper'
import ToggleSwitch from '../../ToggleSwitch/ToggleSwitch'
import { booleanTestIdFormatter } from './../../../utils/helpers'
import GroupFieldTitleToggle from "../HelperComponents/GroupFieldTitleToggle"

export const Boolean = (props: any) => {
  const { onChange, options, parentInfo, uniqueKey, schema, type, isNonLocalizable, hasNonLocalizablePlan, hasNonLocalizablePlanForBlocksAndGroupMultiple } = props
  const [initialSchema, setSchema] = useState(schema)
  const { display_name, uid, field_metadata } = schema
  const [error, setError] = useState(fieldUidRegex(schema.uid, schema))

  const displayNameRef: any = useRef(null)

  useEffect(() => {
    displayNameRef.current.focus()
  }, [displayNameRef])

  const onchange = () => {
    onChange && onChange(schema)
    setSchema({ ...schema })
  }

  const onDisplayNameChange = (event: any) => {
    schema.display_name = event.target.value

    if (schema.hasOwnProperty('display_name') && schema.newfield) {
      schema.uid = schema.display_name
        .trim()
        .replace(/[^A-Z0-9]+/gi, '_')
        .toLowerCase()
      if (schema.display_name) {
        schema.error_details = {
          ...schema.error_details,
          uid: ''
        }
      }
    }

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.display_name.length) {
      schema.error_details = {
        ...schema.error_details,
        display_name: 'Display Name cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        display_name: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onUidChange = (event: any) => {
    setError(fieldUidRegex(event.target?.value?.trim(), schema))

    if (!schema.error_details) {
      schema.error_details = {}
    }

    if (!schema.uid.length) {
      schema.error_details = {
        ...schema.error_details,
        uid: 'uid cannot be empty'
      }
      schema.error = true
    } else {
      schema.error_details = {
        ...schema.error_details,
        uid: ''
      }
      schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
        ? true
        : false
    }
    onchange()
  }
  const onInstructionChange = (event: any) => {
    field_metadata.instruction = event.target.value
    onchange()
  }
  const onHelpTextChange = (event: any) => {
    field_metadata.description = event.target.value
    onchange()
  }
  const onOptionChange = (event: any) => {
    if (event.target.name === `${uniqueKey}.nonLocalization`) {
      if (event.target.checked && parentInfo.hasParent && hasNonLocalizablePlanForBlocksAndGroupMultiple) {
        handleToggleClick(event, parentInfo.data_type, schema, onchange, 'nonLocalizable', props.t , parentInfo.isInsideMultiple ,parentInfo.ancestorWithMultipleType);
      }
      else {
        schema.non_localizable = event.target.checked
      }
    }
    onchange()
  }
  const onDefaultValueChange = (event: any) => {
    schema.field_metadata.default_value = event.target.value === 'true' ? true : false
    onchange()
  }

  if (type === 'basic') {
    return (
      <div className="FormFields">
        <div className="FormFields__column">
          <Field>
            <FieldLabel testId="cs-form-field-boolean-name" version="v2" required htmlFor="displayName">
              Display Name
            </FieldLabel>
            <TextInput
              required
              version="v2"
              name={`${uniqueKey}.displayName`}
              value={display_name}
              inputRef={displayNameRef}
              onChange={onDisplayNameChange}
              error={schema.error && schema.error_details.display_name ? true : false}
              placeholder={props.t(
                'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
              )}
              testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DISPLAY_NAME_INPUT)}
            />
          </Field>
          <Field>
            <FieldLabel testId="cs-form-field-boolean-uid" version="v2" required htmlFor="uid">
              Unique ID
            </FieldLabel>
            <Help testId="cs-form-field-boolean-uid-helptext" type="basic" text="A unique ID for this field. This should not be same as the ID of any other field in this content type." />
            <TextInput
              required
              version="v2"
              name={`${uniqueKey}.uid`}
              value={uid}
              onChange={onUidChange}
              error={(schema.error && schema.error_details.uid) || error ? true : false}
              placeholder={props.t(
                'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
              )}
              testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.UID_INPUT)}
            />
          </Field>
          <Field>
            <FieldLabel testId="cs-form-field-boolean-instruction" version="v2" htmlFor="instruction">Instruction Value</FieldLabel>
            <Help testId="cs-form-field-boolean-instruction-helptext" type="basic" text="Helps you add instructions for this field. The instructional text will appear below the field." />
            <Textarea
              version="v2"
              name={`${uniqueKey}.instruction`}
              value={field_metadata.instruction || ''}
              onChange={onInstructionChange}
              placeholder={props.t(
                'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
              )}
              // multiline={true}
              testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.INSTRUCTION_INPUT)}
              id={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.INSTRUCTION_INPUT)}
            />
          </Field>
          <Field>
            <FieldLabel version="v2" testId="cs-form-field-boolean-helptext" htmlFor="helpText">Help Text</FieldLabel>
            <Help testId="cs-form-field-boolean-helptext-helptext" type="basic" text="Provide additional info about the field. The help text will appear when the user hovers on the ‘?’ sign beside the field name." />
            <Textarea
              version="v2"
              showCharacterCount={true}
              hideCharCountError={false}
              maxLength={256}
              name={`${uniqueKey}.helpText`}
              value={field_metadata.description || ''}
              onChange={onHelpTextChange}
              placeholder={props.t(
                'content_type.save_content_type.body.fields.common_properties.placeholders.enter_value'
              )}
              testId={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DESCRIPTION_INPUT)}
              id={getContentTypeFieldDataTestId(schema, type, CONTENT_TYPE_FIELD_ACTION_IDS.DESCRIPTION_INPUT)}
            />
          </Field>
        </div>
      </div>
    )
  } else if (type === 'advanced') {
    return (
      <div className="FormFields">
        <div className="FormFields__column">
          <Field>
            <FieldLabel testId="cs-form-field-boolean-default" version="v2" htmlFor="defaultValue">Default Value</FieldLabel>
            <div className="ml-12">
              <Radio
                testId="cs-ct-field-boolean-true"
                radioDisplayType="block"
                name={`${uniqueKey}.defaultValue`}
                text="True"
                value="true"
                checked={field_metadata.default_value}
                onChange={onDefaultValueChange}
              />
              <Radio
                testId="cs-ct-field-boolean-false"
                radioDisplayType="block"
                name={`${uniqueKey}.defaultValue`}
                text="False"
                value="false"
                checked={!field_metadata.default_value}
                onChange={onDefaultValueChange}
              />
            </div>
            <GroupFieldTitleToggle
              parentInfo={parentInfo}
              uniqueKey={uniqueKey}
              field_metadata={field_metadata}
              onchange={onchange}
              multiple={schema?.multiple}
              testId="cs-ct-field-boolean-group-title"
            />
          </Field>
        </div>
        <div className="FormFields__column">
          {
            hasNonLocalizablePlan ? (
              hasNonLocalizablePlanForBlocksAndGroupMultiple ? (
                <NonLocalizableDom
                  isFieldPresent={false}
                  uniqueKey={uniqueKey}
                  schema={schema}
                  onOptionsChange={onOptionChange}
                  dataTestId={booleanTestIdFormatter(schema.nonLocParams?.insideMultiple ? `cs-ct-field-boolean-non-localizable-parent-multiple` : `cs-ct-field-boolean-non-localizable`, schema.non_localizable)}
                  dataTestIdForDescription={"cs-ct-field-boolean-non-localizable-description"}
                  isNonLocalizable={isNonLocalizable}
                  isAncestorNonLoc ={parentInfo.isAncestorNonLoc}
                />
              ) : (parentInfo.hasParent ? !parentInfo.isInsideMultiple : true) ?
                <NonLocalizableDom
                  isFieldPresent={false}
                  uniqueKey={uniqueKey}
                  schema={schema}
                  onOptionsChange={onOptionChange}
                  dataTestId={booleanTestIdFormatter("cs-ct-field-boolean-non-localizable", schema.non_localizable)}
                  dataTestIdForDescription={"cs-ct-field-boolean-non-localizable-description"}
                  isNonLocalizable={isNonLocalizable}
                  isAncestorNonLoc ={parentInfo.isAncestorNonLoc}
                />
                : null
            ) : null
          }

          {/* <Field>
						<FieldLabel htmlFor="options">Options</FieldLabel>
						<div className="FormFields__selection-wrapper">
							{(parentInfo.hasParent ? !parentInfo.isMulti : true) ?
								hasNonLocalizablePlan ?
									<>
										{isNonLocalizable ?
											<Tooltip position="top" content="Available only if there are multiple languages in your stack.">
												<Checkbox
													name={`${uniqueKey}.nonLocalization`}
													text="Non-localizable"
													checked={schema.non_localizable}
													disabled={isNonLocalizable}
													onChange={onOptionChange}
												/>
											</Tooltip>
											:
											<Checkbox
												name={`${uniqueKey}.nonLocalization`}
												text="Non-localizable"
												checked={schema.non_localizable}
												disabled={isNonLocalizable}
												onChange={onOptionChange}
											/>
										}
										<div className="nl-note">If enabled, editing this field is restricted in localized entries. The field will use the value of the master-language entry in all localized entries.</div>
									</> : null
								: null}
						</div>
					</Field> */}
        </div>
      </div >
    )
  }
}

export default React.memo(Boolean)
